import React, { useState } from 'react'
import {
  ExternalLink,
  Gamepad2,
  Radio,
  Mail,
  Timer,
  Trophy,
  CheckCircle2,
} from 'lucide-react'

const GAME_URL = 'https://100pour100johnny.com/game'

/*
 * ============================================================
 * PAGE "EN CE MOMENT / JEUX"
 * ============================================================
 *
 * Cette page est indépendante du site 100% Johnny.
 *
 * IMPORTANT :
 * - aucun iframe
 * - aucun lecteur radio
 * - aucun écran "Choisissez votre expérience d'écoute"
 * - le site officiel est ouvert uniquement lorsque l'utilisateur
 *   clique sur le bouton.
 *
 * Le statut ci-dessous est volontairement simple à modifier.
 * Si un nouveau jeu est lancé, mettre active: true et renseigner
 * son titre/description.
 *
 * Pour une vérification 100% automatique, il faudrait que le site
 * officiel fournisse une API publique indiquant le jeu actif.
 */

const CURRENT_GAME = {
  active: false,
  title: '',
  description: '',
}

/*
 * Jeux que l'on peut trouver sur la page officielle.
 * Ils sont présentés ici uniquement pour expliquer les formats.
 */
const GAMES = [
  {
    icon: Radio,
    title: 'Le Mot Magique',
    description:
      "Un mot est annoncé à l'antenne. Écoute Radio 100% Johnny, retrouve le mot puis participe depuis ton téléphone.",
    label: 'Jeu radio',
  },
  {
    icon: Mail,
    title: 'Écris-lui une lettre',
    description:
      "Écris ton message à Johnny et participe à une animation autour des messages envoyés par les fans.",
    label: 'Participation',
  },
  {
    icon: Timer,
    title: '83 Secondes Chrono',
    description:
      'Réponds au maximum de questions sur Johnny Hallyday dans un temps limité.',
    label: 'Quiz chronométré',
  },
]

function EnCeMomentPage() {
  const [showGameModal, setShowGameModal] = useState(false)

  const openGameModal = () => setShowGameModal(true)
  const closeGameModal = () => setShowGameModal(false)

  return (
    <main className="game-page">
      <style>{`
        .game-page {
          min-height: 100dvh;
          width: 100%;
          padding: 20px;
          box-sizing: border-box;
          background:
            radial-gradient(
              circle at 50% -15%,
              rgba(255, 215, 0, .13),
              transparent 38%
            ),
            #050505;
          color: #fff;
          font-family: Arial, Helvetica, sans-serif;
        }

        .game-page *,
        .game-page *::before,
        .game-page *::after {
          box-sizing: border-box;
        }

        .game-container {
          width: min(1080px, 100%);
          margin: 0 auto;
        }

        .game-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 20px 22px;
          border: 1px solid rgba(255, 215, 0, .20);
          border-radius: 20px;
          background: #101010;
        }

        .game-brand {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .game-logo {
          width: 54px;
          height: 54px;
          min-width: 54px;
          display: grid;
          place-items: center;
          border-radius: 15px;
          color: #090909;
          background: linear-gradient(135deg, #ffd700, #ffab00);
          box-shadow: 0 8px 25px rgba(255, 215, 0, .14);
        }

        .game-kicker {
          color: #ffd700;
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 1.7px;
          text-transform: uppercase;
        }

        .game-title {
          margin: 3px 0 0;
          font-size: clamp(28px, 5vw, 42px);
          line-height: 1;
          font-weight: 950;
        }

        .game-site-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 48px;
          padding: 0 16px;
          flex-shrink: 0;
          border: 1px solid rgba(255, 215, 0, .30);
          border-radius: 13px;
          color: #ffd700;
          background: rgba(255, 215, 0, .08);
          text-decoration: none;
          font-size: 13px;
          font-weight: 900;
        }

        .game-site-button:hover {
          background: rgba(255, 215, 0, .14);
        }

        .game-current {
          margin-top: 18px;
          padding: 25px;
          border: 1px solid rgba(255, 215, 0, .20);
          border-radius: 20px;
          background:
            linear-gradient(
              135deg,
              rgba(255, 215, 0, .09),
              rgba(255, 255, 255, .025)
            );
        }

        .game-current-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffd700;
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 1.4px;
          text-transform: uppercase;
        }

        .game-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #777;
        }

        .game-dot.active {
          background: #35d46f;
          box-shadow: 0 0 12px rgba(53, 212, 111, .65);
        }

        .game-current h2 {
          margin: 12px 0 8px;
          font-size: clamp(23px, 4vw, 34px);
          line-height: 1.15;
        }

        .game-current p {
          max-width: 760px;
          margin: 0;
          color: rgba(255, 255, 255, .58);
          line-height: 1.6;
          font-size: 15px;
        }

        .game-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          margin-top: 19px;
          min-height: 48px;
          padding: 0 18px;
          border-radius: 13px;
          color: #080808;
          background: linear-gradient(135deg, #ffd700, #ffab00);
          text-decoration: none;
          font-size: 13px;
          font-weight: 900;
          box-shadow: 0 8px 22px rgba(255, 215, 0, .12);
        }

        .game-section {
          margin-top: 26px;
        }

        .game-section-heading {
          margin-bottom: 16px;
        }

        .game-section-heading h2 {
          margin: 0 0 5px;
          font-size: 25px;
          font-weight: 900;
        }

        .game-section-heading p {
          margin: 0;
          color: rgba(255, 255, 255, .42);
          font-size: 13px;
        }

        .game-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 15px;
        }

        .game-card {
          min-width: 0;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, .09);
          border-radius: 18px;
          background: #111;
        }

        .game-card-icon {
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          margin-bottom: 16px;
          border-radius: 12px;
          color: #ffd700;
          background: rgba(255, 215, 0, .08);
          border: 1px solid rgba(255, 215, 0, .13);
        }

        .game-card h3 {
          margin: 0 0 9px;
          font-size: 19px;
          line-height: 1.2;
        }

        .game-card p {
          margin: 0;
          color: rgba(255, 255, 255, .53);
          font-size: 14px;
          line-height: 1.6;
        }

        .game-card-label {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-top: 16px;
          padding-top: 13px;
          border-top: 1px solid rgba(255, 255, 255, .07);
          color: #ffd700;
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .8px;
        }

        .game-bottom {
          margin-top: 22px;
          padding: 15px;
          border-top: 1px solid rgba(255, 255, 255, .06);
          color: rgba(255, 255, 255, .32);
          font-size: 11px;
          text-align: center;
          line-height: 1.5;
        }


        .game-site-button,
        .game-action {
          cursor: pointer;
          font-family: inherit;
        }

        .game-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(0, 0, 0, .82);
          backdrop-filter: blur(7px);
        }

        .game-modal {
          width: min(1180px, 100%);
          height: min(92dvh, 820px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(255, 215, 0, .25);
          border-radius: 18px;
          background: #080808;
          box-shadow: 0 25px 80px rgba(0, 0, 0, .75);
        }

        .game-modal-header {
          min-height: 58px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 14px 10px 18px;
          flex-shrink: 0;
          border-bottom: 1px solid rgba(255, 255, 255, .08);
          background: #111;
        }

        .game-modal-title {
          display: flex;
          align-items: center;
          gap: 9px;
          color: #ffd700;
          font-size: 14px;
          font-weight: 900;
        }

        .game-modal-close {
          width: 38px;
          height: 38px;
          min-width: 38px;
          display: grid;
          place-items: center;
          border: 1px solid rgba(255, 255, 255, .12);
          border-radius: 10px;
          color: #fff;
          background: #191919;
          cursor: pointer;
          font-family: inherit;
          font-size: 23px;
          line-height: 1;
        }

        .game-modal-close:hover {
          background: #242424;
          border-color: rgba(255, 215, 0, .35);
        }

        .game-modal-frame {
          width: 100%;
          height: 100%;
          flex: 1;
          min-height: 0;
          border: 0;
          background: #050505;
        }

        @media (max-width: 600px) {
          .game-modal-backdrop {
            padding: 0;
          }

          .game-modal {
            width: 100%;
            height: 100dvh;
            border-radius: 0;
            border: 0;
          }

          .game-modal-header {
            min-height: 54px;
          }
        }

        @media (max-width: 800px) {
          .game-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          .game-page {
            padding: 10px;
          }

          .game-header {
            padding: 15px;
            border-radius: 17px;
          }

          .game-logo {
            width: 46px;
            height: 46px;
            min-width: 46px;
          }

          .game-site-button {
            width: 46px;
            min-width: 46px;
            padding: 0;
          }

          .game-site-button span {
            display: none;
          }

          .game-current {
            padding: 19px;
          }

          .game-card {
            padding: 18px;
          }
        }
      `}</style>

      <div className="game-container">
        <header className="game-header">
          <div className="game-brand">
            <div className="game-logo">
              <Gamepad2 size={28} />
            </div>

            <div>
              <div className="game-kicker">100% Johnny</div>
              <h1 className="game-title">En ce moment</h1>
            </div>
          </div>

          <button
            type="button"
            className="game-site-button"
            onClick={openGameModal}
            aria-label="Ouvrir les jeux dans une fenêtre"
          >
            <span>Jeux</span>
            <ExternalLink size={18} />
          </button>
        </header>

        <section className="game-current">
          <div className="game-current-label">
            <span className={`game-dot ${CURRENT_GAME.active ? 'active' : ''}`} />
            {CURRENT_GAME.active ? 'Jeu en cours' : 'Les jeux du moment'}
          </div>

          {CURRENT_GAME.active ? (
            <>
              <h2>{CURRENT_GAME.title}</h2>

              <p>
                {CURRENT_GAME.description ||
                  'Un jeu est actuellement ouvert sur 100% Johnny.'}
              </p>

              <button
                type="button"
                className="game-action"
                onClick={openGameModal}
              >
                Participer au jeu
                <ExternalLink size={17} />
              </button>
            </>
          ) : (
            <>
              <h2>Les jeux du moment</h2>

              <p>
                Découvre les jeux actuellement proposés par 100% Johnny et accède directement à la page officielle pour participer lorsque le jeu est ouvert.
              </p>

              <button
                type="button"
                className="game-action"
                onClick={openGameModal}
              >
                Vérifier les jeux
                <ExternalLink size={17} />
              </button>
            </>
          )}
        </section>

        <section className="game-section">
          <div className="game-section-heading">
            <h2>Les jeux que l’on peut trouver</h2>
            <p>
              Des jeux autour de Johnny Hallyday, de la radio et des fans.
            </p>
          </div>

          <div className="game-grid">
            {GAMES.map((game) => {
              const Icon = game.icon

              return (
                <article className="game-card" key={game.title}>
                  <div className="game-card-icon">
                    <Icon size={22} />
                  </div>

                  <h3>{game.title}</h3>

                  <p>{game.description}</p>

                  <div className="game-card-label">
                    <Trophy size={14} />
                    {game.label}
                  </div>
                </article>
              )
            })}
          </div>
        </section>

        <div className="game-bottom">
          La participation et les jeux actifs sont gérés sur le site officiel
          100% Johnny.
        </div>
      </div>

      {showGameModal && (
        <div
          className="game-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Jeux 100% Johnny"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeGameModal()
          }}
        >
          <div className="game-modal">
            <div className="game-modal-header">
              <div className="game-modal-title">
                <Gamepad2 size={19} />
                Jeux 100% Johnny
              </div>

              <button
                type="button"
                className="game-modal-close"
                onClick={closeGameModal}
                aria-label="Fermer les jeux"
              >
                ×
              </button>
            </div>

            <iframe
              className="game-modal-frame"
              src={GAME_URL}
              title="Jeux 100% Johnny"
              allow="autoplay; fullscreen"
            />
          </div>
        </div>
      )}
    </main>
  )
}

export default EnCeMomentPage